import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const outbuildingsModule: ChecklistModuleDefinition = {
  code: 'outbuildings',
  name: 'Outbuildings',
  description: 'Barns, sheds, shops, and other structures',
  categories: [
    {
      code: 'building_inventory',
      name: 'Building Inventory',
      items: [
        {
          code: 'outbuilding_list',
          title: 'Outbuilding inventory',
          description: 'List all structures on property',
          isGroupTask: false,
          dataFields: [
            { key: 'total_buildings', label: 'Total Buildings', type: 'number' },
            { key: 'building_types', label: 'Building Types', type: 'array' },
            { key: 'total_sqft', label: 'Total Square Feet', type: 'number', unit: 'sq ft' },
          ],
        },
      ],
    },
    {
      code: 'barns',
      name: 'Barns',
      items: [
        {
          code: 'barn_assessment',
          title: 'Barn assessment',
          description: 'Evaluate main barn structure',
          isGroupTask: false,
          dataFields: [
            { key: 'barn_type', label: 'Barn Type', type: 'select', options: [
              { value: 'pole', label: 'Pole Barn' },
              { value: 'traditional', label: 'Traditional Timber Frame' },
              { value: 'metal', label: 'Metal Building' },
              { value: 'bank', label: 'Bank Barn' },
              { value: 'monitor', label: 'Monitor Barn' },
            ]},
            { key: 'year_built', label: 'Year Built', type: 'number' },
            { key: 'dimensions', label: 'Dimensions', type: 'string' },
            { key: 'sqft', label: 'Square Feet', type: 'number', unit: 'sq ft' },
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
              { value: 'condemned', label: 'Condemned/Unsafe' },
            ]},
            { key: 'foundation_condition', label: 'Foundation Condition', type: 'string' },
            { key: 'roof_condition', label: 'Roof Condition', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Well-maintained barn, adds value' },
            { flag: 'yellow', condition: 'Barn needs repairs' },
            { flag: 'red', condition: 'Barn in poor condition' },
            { flag: 'big_red', condition: 'Unsafe structure, liability' },
          ],
        },
        {
          code: 'barn_features',
          title: 'Barn features/utilities',
          description: 'Amenities and utilities in barn',
          isGroupTask: false,
          dataFields: [
            { key: 'has_electric', label: 'Has Electric', type: 'boolean' },
            { key: 'has_water', label: 'Has Water', type: 'boolean' },
            { key: 'has_heating', label: 'Has Heating', type: 'boolean' },
            { key: 'loft_area', label: 'Loft Area', type: 'boolean' },
            { key: 'stalls', label: 'Number of Stalls', type: 'number' },
            { key: 'concrete_floor', label: 'Concrete Floor', type: 'boolean' },
          ],
        },
      ],
    },
    {
      code: 'shops_garages',
      name: 'Shops & Garages',
      items: [
        {
          code: 'shop_assessment',
          title: 'Shop/garage assessment',
          description: 'Workshop and garage buildings',
          isGroupTask: false,
          dataFields: [
            { key: 'building_type', label: 'Building Type', type: 'string' },
            { key: 'dimensions', label: 'Dimensions', type: 'string' },
            { key: 'sqft', label: 'Square Feet', type: 'number', unit: 'sq ft' },
            { key: 'construction', label: 'Construction Type', type: 'string' },
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'overhead_doors', label: 'Overhead Doors', type: 'number' },
            { key: 'door_height', label: 'Door Height', type: 'number', unit: 'feet' },
          ],
        },
        {
          code: 'shop_utilities',
          title: 'Shop utilities',
          description: 'Shop utility services',
          isGroupTask: false,
          dataFields: [
            { key: 'electrical_service', label: 'Electrical Service', type: 'string' },
            { key: 'three_phase', label: 'Three Phase Available', type: 'boolean' },
            { key: 'has_water', label: 'Has Water', type: 'boolean' },
            { key: 'has_drain', label: 'Has Floor Drain', type: 'boolean' },
            { key: 'heating_type', label: 'Heating Type', type: 'string' },
            { key: 'air_compressor_lines', label: 'Air Lines', type: 'boolean' },
          ],
        },
      ],
    },
    {
      code: 'storage_sheds',
      name: 'Storage Structures',
      items: [
        {
          code: 'shed_inventory',
          title: 'Shed/storage inventory',
          description: 'Storage buildings assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'shed_count', label: 'Number of Sheds', type: 'number' },
            { key: 'total_storage_sqft', label: 'Total Storage SF', type: 'number', unit: 'sq ft' },
            { key: 'types', label: 'Types', type: 'array' },
            { key: 'overall_condition', label: 'Overall Condition', type: 'string' },
          ],
        },
        {
          code: 'run_in_shelters',
          title: 'Run-in/loafing sheds',
          description: 'Livestock shelter structures',
          isGroupTask: false,
          dataFields: [
            { key: 'shelter_count', label: 'Number of Shelters', type: 'number' },
            { key: 'total_sqft', label: 'Total Square Feet', type: 'number', unit: 'sq ft' },
            { key: 'condition', label: 'Condition', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'special_structures',
      name: 'Special Purpose Structures',
      items: [
        {
          code: 'chicken_coop',
          title: 'Chicken coop/poultry housing',
          description: 'Poultry facilities',
          isGroupTask: false,
          dataFields: [
            { key: 'exists', label: 'Coop Exists', type: 'boolean' },
            { key: 'capacity', label: 'Capacity', type: 'number', unit: 'birds' },
            { key: 'condition', label: 'Condition', type: 'string' },
          ],
        },
        {
          code: 'greenhouse',
          title: 'Greenhouse/high tunnel',
          description: 'Growing structures',
          isGroupTask: false,
          dataFields: [
            { key: 'exists', label: 'Greenhouse Exists', type: 'boolean' },
            { key: 'type', label: 'Type', type: 'string' },
            { key: 'sqft', label: 'Square Feet', type: 'number', unit: 'sq ft' },
            { key: 'condition', label: 'Condition', type: 'string' },
          ],
        },
        {
          code: 'other_structures',
          title: 'Other structures',
          description: 'Additional unique structures',
          isGroupTask: false,
          dataFields: [
            { key: 'structures', label: 'Other Structures', type: 'array' },
            { key: 'notes', label: 'Notes', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'repair_estimates',
      name: 'Repair Estimates',
      items: [
        {
          code: 'outbuilding_repairs',
          title: 'Outbuilding repair estimates',
          description: 'Cost to repair/improve outbuildings',
          isGroupTask: true,
          dataFields: [
            { key: 'immediate_repairs', label: 'Immediate Repairs', type: 'number', unit: 'dollars' },
            { key: 'deferred_maintenance', label: 'Deferred Maintenance', type: 'number', unit: 'dollars' },
            { key: 'demolition_needed', label: 'Demolition Needed', type: 'boolean' },
            { key: 'demolition_cost', label: 'Demolition Cost', type: 'number', unit: 'dollars' },
            { key: 'total_repairs', label: 'Total Repair Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Significant repair costs' },
          ],
        },
      ],
    },
  ],
};

